import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  Plus,
  MapPin,
  CalendarDays,
  User,
  Bell,
  FileText,
  Heart,
} from 'lucide-react'
import { useNotificationRefresh } from '../../contexts/NotificationContext'
import { usePermissions } from '../../hooks/usePermissions'

export default function BottomNavigation() {
  const location = useLocation()
  const { contadorNoLeidas } = useNotificationRefresh()
  const {
    canAccessRoute,
    isDueno,
    isVeterinario,
    isAdmin,
    isSuperAdmin,
  } = usePermissions()
  const pathname = location.pathname
  const searchParams = new URLSearchParams(location.search)
  const isNuevoCita = pathname === '/citas' && searchParams.get('nuevo') === '1'
  const isCitasPath = pathname === '/citas' || pathname.startsWith('/citas/')
  const isNotifs = pathname === '/notificaciones'
  const isClinicStaff = isVeterinario || isAdmin || isSuperAdmin

  const profileItem = isNotifs
    ? {
        name: 'Alertas',
        href: '/notificaciones',
        icon: Bell,
        active: true,
        badge: contadorNoLeidas > 0 ? (contadorNoLeidas > 9 ? '9+' : String(contadorNoLeidas)) : '',
      }
    : {
        name: 'Perfil',
        href: '/perfil',
        icon: User,
        active: pathname === '/perfil' || pathname.startsWith('/perfil/'),
      }

  let navigationItems

  if (isClinicStaff) {
    navigationItems = [
      {
        name: 'Inicio',
        href: '/',
        icon: Home,
        active: pathname === '/',
      },
      {
        name: 'Agenda',
        href: '/agenda',
        icon: CalendarDays,
        active: pathname === '/agenda' || pathname.startsWith('/agenda/'),
      },
      {
        name: 'Ficha',
        href: '/fichas/nueva',
        icon: Plus,
        active: pathname.startsWith('/fichas'),
        center: true,
        centerLabel: 'Ficha',
      },
      {
        name: 'Citas',
        href: '/citas',
        icon: FileText,
        active: pathname === '/citas' || pathname.startsWith('/citas/'),
      },
      profileItem,
    ]
  } else if (isDueno) {
    navigationItems = [
      {
        name: 'Inicio',
        href: '/',
        icon: Home,
        active: pathname === '/',
      },
      {
        name: 'Clínicas',
        href: '/suscripciones-clinicas',
        icon: MapPin,
        active:
          pathname === '/suscripciones-clinicas' ||
          pathname.startsWith('/suscripciones-clinicas/'),
      },
      {
        name: 'Cita',
        href: '/citas?nuevo=1',
        icon: Plus,
        active: isNuevoCita,
        center: true,
        centerLabel: 'Cita',
      },
      {
        name: 'Citas',
        href: '/citas',
        icon: CalendarDays,
        active: isCitasPath && !isNuevoCita,
      },
      profileItem,
    ]
  } else {
    // PET_SITTER y fallback
    navigationItems = [
      {
        name: 'Inicio',
        href: '/',
        icon: Home,
        active: pathname === '/',
      },
      {
        name: 'Mascotas',
        href: '/mascotas',
        icon: Heart,
        active: pathname === '/mascotas' || pathname.startsWith('/mascotas/'),
      },
      {
        name: 'Cita',
        href: '/citas?nuevo=1',
        icon: Plus,
        active: isNuevoCita,
        center: true,
        centerLabel: 'Cita',
      },
      {
        name: 'Citas',
        href: '/citas',
        icon: CalendarDays,
        active: isCitasPath && !isNuevoCita,
      },
      profileItem,
    ]
  }

  const visibleItems = navigationItems.filter((item) => {
    const path = item.href.split('?')[0]
    // Inicio siempre visible para usuarios autenticados
    if (path === '/' || item.disabled) return true
    return canAccessRoute(path)
  })

  return (
    <nav className="relative z-50 w-full">
      <div className="bg-white border-t border-mobile-border pt-2 pb-3">
        <div className="flex items-end justify-around">
          {visibleItems.map((item) => {
            const isActive = item.active
            const isCenter = item.center === true
            const Component = Link

            return (
              <Component
                key={`${item.name}-${item.href}`}
                to={item.href}
                className={`
                  flex flex-col items-center justify-center gap-0.5 px-1 py-0.5 transition-all duration-200 flex-1
                  ${isCenter ? '-mt-3' : ''}
                `}
              >
                {isCenter ? (
                  <>
                    <span className="h-11 w-11 rounded-full bg-mobile-brand text-white flex items-center justify-center text-[22px] shadow-sm">
                      <item.icon className="w-6 h-6" />
                    </span>
                    <span className="text-[10px] font-medium text-mobile-brand">
                      {item.centerLabel || item.name}
                    </span>
                  </>
                ) : (
                  <>
                    <span className={`relative ${isActive ? 'text-mobile-brand' : 'text-mobile-muted'}`}>
                      <item.icon className="w-5 h-5" />
                      {item.badge ? (
                        <span className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-1 rounded-full bg-mobile-danger text-white text-[9px] leading-[14px] font-bold text-center">
                          {item.badge}
                        </span>
                      ) : null}
                    </span>
                    <span className={`text-[10px] ${isActive ? 'text-mobile-brand' : 'text-mobile-muted'}`}>
                      {item.name}
                    </span>
                  </>
                )}
              </Component>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
